import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useCreateEmployee, useUpdateEmployee } from "@/hooks/useEmployees";
import {
  createEmployeeFormSchema,
  CreateEmployeeFormValues,
  updateEmployeeFormSchema,
  UpdateEmployeeFormValues,
} from "./employee.schemas";
import { Employee } from "@/api/employee.api";
import { isAxiosError } from "axios";

interface EmployeeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: Employee | null;
}

export function EmployeeFormDialog({ open, onOpenChange, employee }: EmployeeFormDialogProps) {
  const isEdit = Boolean(employee);
  const { toast } = useToast();
  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const mutation = isEdit ? updateMutation : createMutation;

  const form = useForm<CreateEmployeeFormValues | UpdateEmployeeFormValues>({
    resolver: zodResolver(isEdit ? updateEmployeeFormSchema : createEmployeeFormSchema),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (open) {
      reset(
        isEdit
          ? {
              fullName: employee?.fullName ?? "",
              phone: employee?.phone ?? "",
              email: employee?.user.email ?? "",
              designation: employee?.designation ?? "",
            }
          : { fullName: "", phone: "", email: "", password: "", designation: "" }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee, isEdit]);

  const onSubmit = async (values: CreateEmployeeFormValues | UpdateEmployeeFormValues) => {
    try {
      if (isEdit && employee) {
        await updateMutation.mutateAsync({ id: employee.id, input: values });
        toast({ title: "Employee updated", variant: "success" });
      } else {
        await createMutation.mutateAsync(values as CreateEmployeeFormValues);
        toast({ title: "Employee account created", variant: "success" });
      }
      onOpenChange(false);
    } catch (err) {
      const message = isAxiosError(err)
        ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
        : undefined;
      toast({ title: "Something went wrong", description: message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit employee" : "Add employee"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this employee's details."
              : "Creates a login account for this technician with the EMPLOYEE role."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="fullName">Full name</Label>
              <Input id="fullName" {...register("fullName")} />
              {errors.fullName && <p className="text-xs text-destructive">{errors.fullName.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" className="font-data" {...register("phone")} />
              {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="designation">Role / designation</Label>
              <Input id="designation" placeholder="Repair Technician" {...register("designation")} />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="email">Login email</Label>
              <Input id="email" type="email" {...register("email")} />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            {!isEdit && (
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="password">Initial password</Label>
                <Input id="password" type="password" {...register("password")} />
                {"password" in errors && errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message as string}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Share this with the employee — they can change it after logging in.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create employee"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
